import { useState, useEffect } from "react";
import { authFetch } from "../api";

const TODAY     = new Date().toLocaleDateString("en-CA");

const sevenDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toLocaleDateString("en-CA");
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA");
};

const SERVICES = [
  { key: "water",           label: "Water",       icon: "💧", color: "#0093dd" },
  { key: "ors",             label: "ORS",         icon: "🧃", color: "#20c997" },
  { key: "mobile_charging", label: "Charging",    icon: "🔌", color: "#6f42c1" },
  { key: "rest_area",       label: "Rest Area",   icon: "🛏️", color: "#fd7e14" },
  { key: "training",        label: "Training",    icon: "📋", color: "#e83e8c" },
  { key: "first_aid",       label: "First Aid",   icon: "🩺", color: "#dc3545" },
  { key: "health_camp",     label: "Health Camp", icon: "🏥", color: "#28a745" },
];

const SERVICE_MAP = Object.fromEntries(SERVICES.map(s => [s.key, s]));

const PAGE_SIZE = 5;

export default function ExitDashboard({ centers: allCenters, apiBase, onClose }) {
  const centers = allCenters.filter(c => c.is_open !== false);
  const [fromDate, setFromDate]   = useState(TODAY);
  const [toDate, setToDate]       = useState(TODAY);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [page, setPage]           = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${apiBase}/exit/summary/all?from_date=${fromDate}&to_date=${toDate}`,
          { signal }
        );
        if (res.ok) setSummaries(await res.json());
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      }
      if (!signal.aborted) setLoading(false);
    };

    fetchAll();
    return () => controller.abort();
  }, [fromDate, toDate]);

  useEffect(() => { setPage(0); }, [search, fromDate, toDate]);

  const filteredCenters = centers
    .filter(c => summaries[c.id]?.total > 0)
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.address.toLowerCase().includes(search.toLowerCase()));
  const totalPages   = Math.ceil(filteredCenters.length / PAGE_SIZE);
  const pagedCenters = filteredCenters.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const grandTotal    = Object.values(summaries).reduce((s, v) => s + (v?.total ?? 0), 0);

  // Aggregate service counts across all centers
  const combinedServices = {};
  const combinedByDate   = {};
  Object.values(summaries).forEach(s => {
    Object.entries(s?.by_service || {}).forEach(([key, count]) => {
      combinedServices[key] = (combinedServices[key] || 0) + count;
    });
    Object.entries(s?.by_date || {}).forEach(([date, count]) => {
      combinedByDate[date] = (combinedByDate[date] || 0) + count;
    });
  });
  const sortedDates  = Object.keys(combinedByDate).sort();
  const maxDayCount  = Math.max(1, ...Object.values(combinedByDate));
  const maxSvcCount  = Math.max(1, ...Object.values(combinedServices));

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Exit Dashboard</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
              {fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>From</label>
            <input type="date" value={fromDate} max={toDate}
              onChange={e => setFromDate(e.target.value)} style={styles.datePicker} />
            <label style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>To</label>
            <input type="date" value={toDate} min={fromDate} max={TODAY}
              onChange={e => setToDate(e.target.value)} style={styles.datePicker} />
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        <div style={styles.body}>

          {/* Grand total + quick range buttons */}
          <div style={styles.totalBanner}>
            <div style={{ display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: 36, fontWeight: 800, color: "#d94f2b" }}>{grandTotal}</span>
              <span style={{ fontSize: 14, color: "#555", marginLeft: 10 }}>
                total exits across {centers.length} center{centers.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { label: "Today",   from: TODAY,          to: TODAY },
                { label: "7 days",  from: sevenDaysAgo(), to: TODAY },
                { label: "30 days", from: daysAgo(29),    to: TODAY },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => { setFromDate(r.from); setToDate(r.to); }}
                  style={styles.rangeBtn(fromDate === r.from && toDate === r.to)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {!loading && centers.length > 0 && (
            <input
              type="search"
              placeholder="Search centers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          )}

          {loading && <div style={styles.empty}>Loading…</div>}

          {/* Overall service usage breakdown */}
          {!loading && grandTotal > 0 && (
            <div style={styles.card}>
              <div style={styles.sectionLabel}>Service Usage — All Centers</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {SERVICES.map(s => {
                  const count = combinedServices[s.key] || 0;
                  const pct   = Math.round((count / maxSvcCount) * 100);
                  return (
                    <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18, width: 24 }}>{s.icon}</span>
                      <span style={{ width: 88, fontSize: 13, color: "#444" }}>{s.label}</span>
                      <div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 10, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: s.color, borderRadius: 4, transition: "width 0.4s" }} />
                      </div>
                      <span style={{ width: 36, fontSize: 13, fontWeight: 600, textAlign: "right", color: "#1a1a1a" }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Day-by-day trend */}
          {!loading && sortedDates.length > 1 && (
            <div style={styles.card}>
              <div style={styles.sectionLabel}>Daily Trend</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, overflowX: "auto", paddingBottom: 4 }}>
                {sortedDates.map(date => {
                  const count = combinedByDate[date] || 0;
                  const pct   = Math.round((count / maxDayCount) * 100);
                  return (
                    <div key={date} style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 36 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#333", marginBottom: 3 }}>{count}</div>
                      <div style={{ width: 28, height: Math.max(4, pct * 0.8), background: "#d94f2b", borderRadius: "3px 3px 0 0" }} />
                      <div style={{ fontSize: 10, color: "#888", marginTop: 4, writingMode: "vertical-rl", transform: "rotate(180deg)", lineHeight: 1 }}>
                        {date.slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && grandTotal === 0 && (
            <div style={styles.empty}>No exit entries for this date range.</div>
          )}

          {!loading && grandTotal > 0 && filteredCenters.length === 0 && (
            <div style={styles.empty}>No centers match "{search}".</div>
          )}

          {/* Per-center cards */}
          {!loading && pagedCenters.map(c => (
            <CenterCard key={c.id} center={c} summary={summaries[c.id]} />
          ))}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                style={styles.pageBtn(page === 0)}
              >
                ← Prev
              </button>
              <span style={{ fontSize: 13, color: "#555" }}>
                Page {page + 1} of {totalPages} · {filteredCenters.length} centers
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                style={styles.pageBtn(page >= totalPages - 1)}
              >
                Next →
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function CenterCard({ center, summary }) {
  const byService = summary.by_service || {};
  const byDate    = summary.by_date    || {};
  const total     = summary.total      || 0;

  const sortedDates = Object.keys(byDate).sort();
  const maxDay      = Math.max(1, ...Object.values(byDate));
  const maxSvc      = Math.max(1, ...Object.values(byService));

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{center.name}</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{center.address}</div>
        </div>
        <div style={{ ...styles.totalBadge, background: "#fff0ed", color: "#d94f2b" }}>{total}</div>
      </div>

      {/* Services breakdown */}
      <div style={styles.sectionLabel}>Services Used</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {SERVICES.map(s => {
          const count = byService[s.key] || 0;
          if (count === 0) return null;
          const pct = Math.round((count / maxSvc) * 100);
          return (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, width: 20 }}>{s.icon}</span>
              <span style={{ width: 80, fontSize: 12, color: "#555" }}>{s.label}</span>
              <div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 8 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: s.color, borderRadius: 4 }} />
              </div>
              <span style={{ width: 28, fontSize: 12, fontWeight: 600, textAlign: "right" }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Daily trend for this center */}
      {sortedDates.length > 1 && (
        <>
          <div style={styles.sectionLabel}>Daily Exits</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, overflowX: "auto" }}>
            {sortedDates.map(date => {
              const count = byDate[date] || 0;
              const pct   = Math.round((count / maxDay) * 100);
              return (
                <div key={date} style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 32 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#333", marginBottom: 2 }}>{count}</div>
                  <div style={{ width: 24, height: Math.max(4, pct * 0.6), background: "#d94f2b", borderRadius: "3px 3px 0 0" }} />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 3, writingMode: "vertical-rl", transform: "rotate(180deg)", lineHeight: 1 }}>
                    {date.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    zIndex: 1000, padding: 16, overflowY: "auto",
  },
  panel: {
    background: "#f5f5f3", borderRadius: 16,
    width: "100%", maxWidth: 900,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    marginTop: 24, marginBottom: 24, overflow: "hidden",
  },
  header: {
    background: "#8b2500", color: "#fff",
    padding: "14px 24px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 10,
  },
  datePicker: {
    padding: "5px 8px", borderRadius: 7, border: "none",
    fontSize: 13, background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 20, cursor: "pointer", color: "#fff", opacity: 0.8,
  },
  body: { padding: 20 },
  totalBanner: {
    background: "#fff", borderRadius: 12, padding: "16px 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 12, marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  rangeBtn: (active) => ({
    padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
    border: active ? "2px solid #d94f2b" : "2px solid #e0e0e0",
    background: active ? "#fff0ed" : "#fafafa",
    color: active ? "#8b2500" : "#555", fontWeight: active ? 700 : 400,
  }),
  empty: { textAlign: "center", color: "#888", padding: "40px 0", fontSize: 14 },
  searchInput: {
    width: "100%", padding: "10px 14px", marginBottom: 16,
    border: "1.5px solid #ddd", borderRadius: 8, fontSize: 14,
    boxSizing: "border-box", outline: "none",
  },
  pagination: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 0", marginTop: 4,
  },
  pageBtn: (disabled) => ({
    padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: "1.5px solid #ddd", background: disabled ? "#f5f5f5" : "#fff",
    color: disabled ? "#bbb" : "#333", cursor: disabled ? "not-allowed" : "pointer",
  }),
  card: {
    background: "#fff", borderRadius: 12, padding: 20, marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 16,
  },
  totalBadge: {
    fontWeight: 800, fontSize: 22, borderRadius: 10,
    padding: "4px 14px", minWidth: 48, textAlign: "center",
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "#888",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
  },
};
